import { nodeToString } from 'src/modules/shared/utils/formatting/node.util';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import TextInput from '../text-input';
import { Popover } from '../../../molecules/popovers/popover';
import { EmptyState } from '../../states/empty-state';
import type { ContainerPopoverPlacement } from 'src/modules/shared/types/popover.type';
import {
  type BaseProps,
  type ComponentAppearance,
  type ComponentSize,
  type ComponentVariant,
} from 'src/modules/shared/types/component.type';
import {
  baseTriggerInputClasses,
  controlTriggerLabelLineHeightClasses,
  controlTriggerPaddingClasses,
  controlTriggerSizeClasses,
  getDisabledClasses,
  getInputAppearanceClasses,
} from 'src/modules/shared/styles/component.styles';
import {
  getMenuItemClasses,
  getMenuItemContentClasses,
  getMenuItemContentColor,
  getMenuItemContentWeight,
  separatorClasses,
} from 'src/modules/shared/styles/dropdown.styles';
import { useSearchableFilter } from 'src/modules/shared/hooks/searchable-filter';
import { useKeyboardNavigation } from 'src/modules/shared/hooks/keyboard-navigation';
import { useControlledValue } from 'src/modules/shared/hooks/controlled-value';
import { PiCaretDown, PiMagnifyingGlass } from 'react-icons/pi';
import Loading from '../../loading';
import { Text } from 'src/modules/shared/components/atoms/text';
import { getControlTextSize } from 'src/modules/shared/components/atoms/text/text.styles';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

/**
 * SearchableSelect component
 *
 * A searchable select input with keyboard navigation, debounced client-side filtering,
 * and accessible list navigation. It uses the shared `TextInput` styles and `Popover` for the menu.
 *
 * - Fully controlled or uncontrolled (via `value` and `onChange` or `defaultValue`)
 * - Debounced search input
 * - Keyboard navigation: ArrowUp/Down, Enter to select, Escape to close
 * - Highlighted active option and selected state
 */
export interface SearchableSelectOption {
  /** Display content for the option */
  content: React.ReactNode | string;
  /** Plain text for filtering rich option content. */
  textValue?: string;
  /** Unique option value */
  value: string;
  /** Optional keywords used for searching (in addition to visible content) */
  keywords?: string;
  /** Disable selection for this option */
  disabled?: boolean;
}

export interface SearchableSelectSeparator {
  /** Render a visual separator row */
  separator: true;
  /** Optional stable key (if omitted, an index-based fallback is used) */
  key?: string;
  /** Optional content (unused) */
  content?: null;
}

export type SearchableSelectItem =
  | SearchableSelectOption
  | SearchableSelectSeparator;

export interface SearchableSelectProps extends Omit<BaseProps, 'children'> {
  /** Notifies the owner when the menu opens, including keyboard activation. */
  onOpenChange?: (open: boolean) => void;
  /** Available options */
  options: SearchableSelectItem[];
  /** Current selected value (controlled) */
  value?: string;
  /** Initial value (uncontrolled) */
  defaultValue?: string;
  /** Fires when the selected value changes */
  onChange?: (value: string | undefined) => void;
  /** Placeholder for the input */
  placeholder?: string;
  /** Input size */
  size?: ComponentSize;
  /** Input variant (borders/focus) */
  variant?: ComponentVariant;
  /** Visual appearance shared with buttons/other controls */
  appearance?: ComponentAppearance;
  /** Popover max height (px) */
  menuMaxHeight?: number;
  /** Popover minimum width (px). Useful when the trigger is too small. */
  menuMinWidth?: number;
  /** Popover maximum width (px). */
  menuMaxWidth?: number;
  /** Optional leading icon rendered inside the trigger */
  leadingIcon?: React.ReactNode;
  /** Popover placement */
  placement?: ContainerPopoverPlacement;
  /**
   * Called when the internal search query changes (debounced).
   * Useful for server-backed option loading.
   */
  onQueryChange?: (query: string) => void;
  /**
   * When false, disables client-side filtering and renders options as provided.
   * Intended for server-backed search where the parent updates `options` based on `onQueryChange`.
   */
  clientFilter?: boolean;
  /** Optional empty message */
  emptyLabel?: string;
  /** Class applied to the trigger container */
  containerClassName?: string;
  /** Render function for option rows */
  renderOption?: (
    option: SearchableSelectOption,
    isActive: boolean,
    isSelected: boolean
  ) => React.ReactNode;
}

const DEBOUNCE_MS = 300;

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  defaultValue,
  onChange,
  placeholder,
  size = 'md',
  variant = 'primary',
  appearance = 'outline',
  disabled = false,
  loading = false,
  menuMaxHeight = 240,
  menuMinWidth,
  menuMaxWidth,
  leadingIcon,
  placement = 'bottom-start',
  onQueryChange,
  clientFilter = true,
  emptyLabel,
  className = '',
  containerClassName = '',
  renderOption,
  onOpenChange,
}) => {
  const { t } = useI18n();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => onOpenChange?.(open), [open, onOpenChange]);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [triggerWidth, setTriggerWidth] = useState<number | null>(null);

  const [selectedValue, setSelectedValue] = useControlledValue(
    value,
    defaultValue,
    onChange
  );
  const selectableOptions = useMemo(
    () =>
      options.filter(
        (item): item is SearchableSelectOption => !('separator' in item)
      ),
    [options]
  );

  const filteredSelectable = useSearchableFilter(
    selectableOptions,
    query,
    DEBOUNCE_MS
  );

  useEffect(() => {
    if (!onQueryChange) return;

    const timeoutId = window.setTimeout(() => {
      onQueryChange(query);
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [onQueryChange, query]);

  // When searching, separators are intentionally hidden (search results are a flat list of matches).
  // When not searching, render the original list (including separators).
  const displayedItems: SearchableSelectItem[] =
    query && clientFilter ? filteredSelectable : options;

  const displayedSelectableOptions =
    query && clientFilter ? filteredSelectable : selectableOptions;

  const selectedOption = useMemo(
    () => selectableOptions.find((o) => o.value === selectedValue),
    [selectableOptions, selectedValue]
  );

  const handleSelect = (opt: SearchableSelectOption | undefined) => {
    if (!opt || opt.disabled) return;
    setSelectedValue(opt.value);
    setOpen(false);
  };

  const { listRef, handleKeyDown } = useKeyboardNavigation({
    isOpen: open,
    itemsLength: displayedSelectableOptions.length,
    activeIndex,
    setActiveIndex,
    onSelect: () => handleSelect(displayedSelectableOptions[activeIndex]),
    onClose: () => setOpen(false),
    onOpen: () => setOpen(true),
  });

  useEffect(() => {
    if (open) {
      setActiveIndex(0);
    }
  }, [displayedSelectableOptions, open]);

  useEffect(() => {
    if (!open) return;

    window.requestAnimationFrame(() => {
      searchInputRef.current?.focus({ preventScroll: true });
    });
  }, [open]);

  const measure = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setTriggerWidth(rect.width);
    }
  };

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, size, variant, options, selectedOption]);

  const triggerClasses = [
    ...baseTriggerInputClasses,
    'gap-2',
    getInputAppearanceClasses(variant, appearance),
    controlTriggerSizeClasses[size],
    controlTriggerPaddingClasses[size],
    'flex items-center',
    'text-left',
    'pr-2',
    getDisabledClasses(disabled),
    className,
  ].join(' ');

  const computedMenuWidth = useMemo(() => {
    const base = triggerWidth ?? menuMinWidth ?? null;
    if (base == null) return null;

    let width = base;
    if (typeof menuMinWidth === 'number') width = Math.max(width, menuMinWidth);
    if (typeof menuMaxWidth === 'number') width = Math.min(width, menuMaxWidth);
    return width;
  }, [menuMaxWidth, menuMinWidth, triggerWidth]);

  const renderRow = (
    opt: SearchableSelectOption,
    isActive: boolean,
    isSelected: boolean
  ) => {
    if (renderOption) return renderOption(opt, isActive, isSelected);

    const contentClasses = getMenuItemContentClasses();
    const textClasses = `${contentClasses} !text-left w-full whitespace-normal break-words`;
    const textSize = getControlTextSize(size);

    return typeof opt.content === 'string' ? (
      <Text
        size={textSize}
        weight={getMenuItemContentWeight(isSelected)}
        color={getMenuItemContentColor(isSelected)}
        className={textClasses}
      >
        {opt.content}
      </Text>
    ) : (
      <div className={textClasses}>{opt.content}</div>
    );
  };

  const handleTriggerClick = () => {
    if (!disabled) {
      measure();
      setOpen(true);
      setQuery('');
    }
  };

  const resolvedPlaceholder =
    placeholder ?? t('shared.searchableSelect.placeholder');
  const resolvedEmptyLabel =
    emptyLabel ?? t('shared.searchableSelect.emptyLabel');

  return (
    <div className={`relative ${containerClassName}`}>
      <Popover
        handler={
          <button
            type="button"
            ref={triggerRef}
            onClick={handleTriggerClick}
            disabled={disabled}
            aria-expanded={open}
            aria-haspopup="listbox"
            className={triggerClasses}
          >
            {leadingIcon && (
              <span className="mr-2 text-secondary-500 flex-shrink-0">
                {leadingIcon}
              </span>
            )}
            <span
              className={`truncate text-left flex-1 ${controlTriggerLabelLineHeightClasses[size]}`}
            >
              {selectedOption?.textValue !== undefined
                ? selectedOption.content
                : nodeToString(selectedOption?.content ?? '') ||
                  resolvedPlaceholder}
            </span>
            <PiCaretDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          </button>
        }
        open={open}
        onOpenChange={(open) => {
          setOpen(open);
          if (!open) setQuery('');
        }}
        contentClassName="p-0 z-[10000] !max-w-none !w-auto"
        contentStyle={
          computedMenuWidth == null
            ? undefined
            : {
                width: computedMenuWidth,
                minWidth: computedMenuWidth,
                maxWidth: computedMenuWidth,
              }
        }
        placement={placement}
        className="w-full"
      >
        <div
          className="flex flex-col"
          style={
            computedMenuWidth == null
              ? undefined
              : {
                  width: computedMenuWidth,
                  minWidth: computedMenuWidth,
                  maxWidth: computedMenuWidth,
                }
          }
        >
          {/* Search input overlay - Notion style */}
          <div className="p-2 border-b border-neutral-100">
            <TextInput
              ref={searchInputRef}
              value={query}
              placeholder={t('shared.searchableSelect.searchPlaceholder')}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              size={size}
              variant={variant}
              className="w-full"
            />
          </div>

          <div
            className="flex flex-col gap-1 p-1 overflow-auto"
            style={{ maxHeight: menuMaxHeight }}
            ref={listRef}
          >
            {loading && (
              <div role="status" className="flex justify-center p-3">
                <Loading size="sm" />
              </div>
            )}
            {!loading && displayedSelectableOptions.length === 0 && (
              <EmptyState
                title={resolvedEmptyLabel}
                description={t('shared.searchableSelect.emptyDescription')}
                icon={<PiMagnifyingGlass className="w-4 h-4" />}
                size="xs"
                className="py-6"
              />
            )}
            {(() => {
              let selectableIndex = 0;

              return displayedItems.map((item, displayedIndex) => {
                if ('separator' in item) {
                  return (
                    <hr
                      key={item.key ?? `separator-${displayedIndex}`}
                      className={separatorClasses}
                    />
                  );
                }

                const opt = item;
                const isSelected = opt.value === selectedValue;
                const isActive = selectableIndex === activeIndex;
                const rowClasses = getMenuItemClasses(
                  size,
                  isSelected,
                  isActive,
                  opt.disabled
                );

                const currentIndex = selectableIndex;
                selectableIndex += 1;

                return (
                  <button
                    type="button"
                    key={opt.value}
                    data-index={currentIndex}
                    className={rowClasses}
                    onMouseEnter={() => setActiveIndex(currentIndex)}
                    onClick={() => handleSelect(opt)}
                    disabled={!!opt.disabled}
                  >
                    {renderRow(opt, isActive, isSelected)}
                  </button>
                );
              });
            })()}
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default SearchableSelect;
